import { observable, computed, action, makeObservable } from 'mobx';
import { configurationDataManager } from 'singletons';
import { AmountCode, BudgetSettingModalViews, ConfigViews, EligibilityProrationTabs } from 'typings/enums';
import { ICycleConfiguration } from 'typings/cycleConfiguration';
import { CycleBudgetFieldFactory } from 'nomst/Stores/Configuration/cycleBudgetFieldFactory';
import { CycleBudgetField } from 'nomst/Stores/Configuration/cycleBudgetField';
import { CycleBudget } from 'nomst/Stores/Configuration/cycleBudget';
import { NumberValue } from 'nomst/Stores/Utility/numberValue';
import { EligibilityProrationOverrideStore } from 'nomst/Stores/Decision/eligibilityProrationOverrideStore';
import { Configuration } from 'nomst/Stores/Configuration/configuration';
import { ConfigurationsStore } from 'nomst/Stores/Configuration/configurations.store';
import { EligibilityProrationRuleView } from './eligibilityProrationRuleView';
import { ICycleBudget } from 'typings/cycleBudget';
import { setCurrentBudgetView } from 'nomst/Actions/configurations';
import { Format } from 'nomst/Stores/Utility/format';
import { ICycleBudgetField } from 'typings/cycleBudgetField';
import { IBudgetServiceManager } from 'typings/dataManagers';
import { dataManagerFactory } from 'utils/MobxDataManager/singletons';

type budgetOnUpdateCallback = (budget: CycleBudget) => void;

export class BudgetConfigurationViewStore {
    @observable editableCycleConfiguration: Nullable<Configuration>;
    @observable currentModalBudget: Nullable<CycleBudget>;
    @observable showBudgetSettingsModal: boolean;
    @observable showDeleteBudgetConfirmation: boolean;
    @observable currentModalView: BudgetSettingModalViews;
    @observable currentSetBudget: Nullable<CycleBudget>;
    @observable overlappedFieldBudgets: Map<string, CycleBudget>;
    @observable fieldsWithOtherProrationRule: string[];
    @observable selectedFieldsHaveDecisions: string[];
    @observable availableCycleBudgetFields: CycleBudgetField[];
    @observable stockField: string;
    @observable stockValueInput: NumberValue;
    @observable eligibilityProrationRuleViewStore: EligibilityProrationOverrideStore;
    cycleBudgetFieldFactory: CycleBudgetFieldFactory;
    @observable isSaving: boolean;
    @observable multiComponentBonus: boolean;
    @observable bonusFieldRules: IBonusFieldRule[];
    @observable configurationsStore: ConfigurationsStore;
    onUpdateCallback: budgetOnUpdateCallback|null = null;
    @observable eligibilityProrationRuleView: EligibilityProrationRuleView;
    _budgetServiceManager: IBudgetServiceManager;
    @observable currentBudgetView: string;

    constructor(
        configStore: ConfigurationsStore,
        format: Format) {
        makeObservable(this);
        this.configurationsStore = configStore;
        this.showBudgetSettingsModal = false;
        this.showDeleteBudgetConfirmation = false;
        this.currentModalView = BudgetSettingModalViews.Create;
        this.fieldsWithOtherProrationRule = [];
        this.selectedFieldsHaveDecisions = [];
        this.availableCycleBudgetFields = [];
        this.stockValueInput = new NumberValue(format);
        this.isSaving = false;
        this.multiComponentBonus = false;
        this.bonusFieldRules = [];
        this.overlappedFieldBudgets = new Map();
        this.cycleBudgetFieldFactory = new CycleBudgetFieldFactory();
        this.currentBudgetView = '';
        this.eligibilityProrationRuleView =
            new EligibilityProrationRuleView(configStore.currentConvertedConfiguration);
        this._budgetServiceManager = dataManagerFactory.getBudgetServiceMaanger();
        this.availableCycleBudgetFields = this.cycleBudgetFieldFactory.getAllMetadata().filter(field => {
            field.amountCode !== AmountCode.Stock;
        });
        if (!this.availableCycleBudgetFields) {
            return;
        }
        this.stockField =
            this.availableCycleBudgetFields?.find(
                cycleBudgetField => cycleBudgetField.amountCode === AmountCode.Stock)?.title ?? AmountCode.Stock;
        this.stockValueInput.setOnChangeCallback(this.handleStockValueChange.bind(this));
    }

    @action
    setCurrentBudgetView(view: string): void {
        this.currentBudgetView = view;
    }

    @action
    setNewCycleConfiguration(): void {
        this.fieldsWithOtherProrationRule = [];
        this.selectedFieldsHaveDecisions = [];
        this.availableCycleBudgetFields = [];
        this.overlappedFieldBudgets = new Map();
    }
    
    @computed
    get currentCycleConfiguration(): Nullable<ICycleConfiguration> {
        return this.configurationsStore.cycleSettingsViewCycle;
    }

    @computed
    get currentConvertedConfiguration(): Nullable<Configuration> {
        return this.configurationsStore.convertedCycleSettingsViewCycle;
    }

    @computed
    get stockFieldSelected(): boolean {
        if (!this.currentModalBudget || !this.currentModalBudget.associatedFields) {
            return false;
        }
        return this.currentModalBudget.associatedFields.length > 0 &&
            this.currentModalBudget.associatedFields[0] === this.stockField;
    }

    @computed
    get availableFields(): string[] {
        return this.availableCycleBudgetFields.map(cycleBudgetField => cycleBudgetField.title);
    }

    getAssociatedFieldCode(fieldTitle: string): AmountCode|undefined {
        if (!this.availableCycleBudgetFields || !this.availableCycleBudgetFields.length) {
            return undefined;
        }
        return this.availableCycleBudgetFields?.find(
            cycleBudgetField => cycleBudgetField.title === fieldTitle)?.amountCode;
    }

    @computed
    get currentModalBudgetProrationRuleId(): number|undefined {
        if (!this.currentModalBudget) {
            return undefined;
        }
        const amountWithProrationRule = this.currentConvertedConfiguration?.cycleBudgetFields
            .find(budgetField =>
                budgetField.budgetCycleId === this.currentModalBudget?.budgetCycleId &&
                budgetField.hasProrationRule);

        return amountWithProrationRule ?
            amountWithProrationRule?.prorationRule?.prorationRuleId ?? undefined : undefined;
    }

    currentBudgetCode(budget: CycleBudget): Nullable<string> {
        if (!budget || !budget.topPriorityField) {
            return null;
        }
        return budget.topPriorityField.amountCode;
    }

    getAmountProrationRuleId(field: string): number|undefined {
        const amountWithProrationRule = this.currentConvertedConfiguration?.cycleBudgetFields
            .find(budgetField =>
                budgetField.title === field &&
                budgetField.hasProrationRule);

        return amountWithProrationRule ?
            amountWithProrationRule?.prorationRule?.prorationRuleId ?? undefined : undefined;
    }

    @action
    setMultiComponentBonus(value: boolean): void {
        this.multiComponentBonus = value;
    }

    @action
    setBonusFieldRules(rules: IBonusFieldRule[]): void {
        this.bonusFieldRules = rules;
    }

    @action
    initBonusFieldRules(): void {
        if (this.bonusFieldRules.length > 0) {
            return;
        }
        this.bonusFieldRules = [{} as IBonusFieldRule,
            {} as IBonusFieldRule,
            {} as IBonusFieldRule,
            {} as IBonusFieldRule];
    }

    @action
    setBonsuRuleName(rule: IBonusFieldRule, value: string): void {
        rule.fieldName = value;
    }

    @action
    setBonsuRuleSplit(rule: IBonusFieldRule, value: number): void {
        rule.split = value;
    }

    @action
    setBonusRuleAttained(rule: IBonusFieldRule, value: number): void {
        rule.attained = value;
    }

    @action
    removeEmptyRules(): void {
        this.bonusFieldRules.forEach((rule) => {
            if (!rule.fieldName) {
                this.bonusFieldRules.filter(bonusRule => bonusRule !== rule);
            }
        });
    }

    @action
    handleStockValueChange(value: number): void {
        const stock = this.editableCycleConfiguration?.getCycleBudgetField(AmountCode.Stock);
        if (!stock) {
            return;
        }

        stock.setStockValue(value);
    }

    @action
    setEditableCycleConfiguration(): void {
        if (this.currentConvertedConfiguration) {
            this.editableCycleConfiguration = this.currentConvertedConfiguration;
        }
    }

    @action
    setEditableCycleConfigurationTest(config: Configuration): void {
        this.editableCycleConfiguration = config;
    }

    @action
    setStockValueInput(): void {
        if (!this.stockFieldSelected) {
            this.stockValueInput.setValue(null);
            return;
        }

        const stock = this.editableCycleConfiguration?.getCycleBudgetField(AmountCode.Stock);
        if (stock && !stock.isUnitOnly) {
            this.stockValueInput.setValue(stock.stockValue);
            return;
        }

        this.stockValueInput.setValue(null);
    }

    @action
    openCreateBudgetModal(): void {
        this.setEditableCycleConfiguration();
        this.currentModalView = BudgetSettingModalViews.Create;
        this.currentModalBudget = new CycleBudget({
            id: this.editableCycleConfiguration?.cycleBudgets.length ?? 0 + 1,
            associatedFields: [] as string[],
            name: '',
            budgetCycleId: this.editableCycleConfiguration?.cycleBudgets.length ?? 0 + 1,
            color: '',
            cycleId: this.editableCycleConfiguration?.cycleId ?? 0,
            cycleBudgetFields: [] as ICycleBudgetField[]
        } as ICycleBudget);
        this.setStockValueInput();
        this.updateAssociatedAmounts(this.currentModalBudget);
        this.availableCycleBudgetFields = this.cycleBudgetFieldFactory.getAllMetadata();
        this.selectedFieldsHaveDecisions = this.currentModalBudget.associatedFields;
        this.stockField =
            this.availableCycleBudgetFields?.find(
                cycleBudgetField => cycleBudgetField.amountCode === AmountCode.Stock)?.title ?? AmountCode.Stock;
        this.showBudgetSettingsModal = true;
    }

    @action
    openEditBudgetModal(budget: CycleBudget, callback: budgetOnUpdateCallback): void {
        this.setEditableCycleConfiguration();
        this.currentModalView = BudgetSettingModalViews.Edit;
        this.currentModalBudget = budget;
        this.availableCycleBudgetFields = this.cycleBudgetFieldFactory.getAllMetadataButStock();
        this.setStockValueInput();
        this.showBudgetSettingsModal = true;
        this.updateAssociatedAmounts(budget);
        this.onUpdateCallback = callback;
    }

    @action
    closeBudgetSettingsModal() {
        if (this.isSaving) {
            return;
        }

        this.showBudgetSettingsModal = false;
        this.currentModalBudget = null;
        this.onUpdateCallback = null;
        this.overlappedFieldBudgets.clear();
        this.fieldsWithOtherProrationRule = [];
        this.selectedFieldsHaveDecisions = [];
    }

    @action
    updateBudgetType(associatedFields: string[]): void {
        const selectedCycleBudgetFields = this.availableCycleBudgetFields
            .filter(cycleBudgetField => associatedFields.some(value => value === cycleBudgetField.title));
        selectedCycleBudgetFields.forEach((selectedCycleBudgetField) => {
            this.editableCycleConfiguration?.addCycleBudgetField(selectedCycleBudgetField.amountCode);
        });
        this.setStockValueInput();
    }

    @action
    tryUpdateFieldsWithOtherProrationRule(selectedField: string): void {
        if (!this.currentModalBudgetProrationRuleId ||
            !this.getAmountProrationRuleId(selectedField)) {
            return;
        }

        if (this.currentModalBudgetProrationRuleId !== this.getAmountProrationRuleId(selectedField)) {
            this.fieldsWithOtherProrationRule.push(selectedField);
        }
    }

    @action
    tryUpdateOverlappedFields(budget: ICycleBudget, selectedField: string): void {
        if (budget.budgetCycleId !== this.currentModalBudget?.budgetCycleId &&
            budget.associatedFields?.indexOf(selectedField) !== -1) {
            this.overlappedFieldBudgets.set(selectedField, new CycleBudget(budget));
        }
    }

    @action
    updateAssociatedFields(associatedFields: string[]): void {
        this.updateBudgetType(associatedFields);
        this.currentModalBudget?.handleAssociatedFieldsChange(associatedFields);
        this.overlappedFieldBudgets.clear();
        this.fieldsWithOtherProrationRule = [];
        this.selectedFieldsHaveDecisions = [];
        this.currentModalBudget?.associatedFields.forEach(selectedField => {
            if (this.currentModalBudget?.useProration) {
                this.tryUpdateFieldsWithOtherProrationRule(selectedField);
                return;
            }

            this.editableCycleConfiguration?.cycleBudgets.forEach(budget => {
                this.tryUpdateOverlappedFields(budget, selectedField);
            });
        });
    }

    amountAlreadyHasDecisions(associatedField: string): boolean {
        if (!this.currentModalBudget?.amountsAlreadyHaveDecisions) {
            return false;
        }

        return this.currentModalBudget.amountsAlreadyHaveDecisions.indexOf(associatedField) > -1;
    }

    getRemovedAssociatedFields(values: string[]): string[] {
        return this.currentModalBudget?.associatedFields
            .filter(item => values.indexOf(item) < 0) ?? [];
    }

    trySetFieldsHaveDecisions(fields: string[]): boolean {
        this.selectedFieldsHaveDecisions = [];
        fields.forEach(field => {
            if (this.amountAlreadyHasDecisions(field)) {
                this.selectedFieldsHaveDecisions.push(field);
            }
        });
        if (this.selectedFieldsHaveDecisions.length > 0) {
            return true;
        }

        return false;
    }

    @action
    removeDeselectedBudgetType(associatedFields: string[]): void {
        const selectedCycleBudgetFields = this.availableCycleBudgetFields
            .filter(cycleBudgetField => associatedFields.some(value => value === cycleBudgetField.title));
        selectedCycleBudgetFields.forEach((selectedCycleBudgetField) => {
            this.editableCycleConfiguration?.removeCycleBudgetField(selectedCycleBudgetField.amountCode);
        });
    }

    @action
    handleAssociatedFieldsChange(values: string[]): void {
        const fields = this.getRemovedAssociatedFields(values);
        this.removeDeselectedBudgetType(fields);
        if (this.trySetFieldsHaveDecisions(fields)) {
            return;
        }

        if (this.stockFieldSelected) {
            return;
        }

        this.updateAssociatedFields(values);
    }

    @action
    handleStockAssociatedFieldChange(): void {
        if (this.stockFieldSelected) {
            this.updateAssociatedFields([]);
            return;
        }

        if (this.currentModalBudget?.associatedFields.length ?? 0 > 0) {
            return;
        }

        this.updateAssociatedFields([this.stockField]);
    }

    @action
    resetView(navigate: boolean = false): void {
        this.currentSetBudget = null;
        this.currentModalBudget = null;
        this.editableCycleConfiguration = null;
        return setCurrentBudgetView('', navigate);
    }

    @action
    openDeleteBudgetConfirmationModal(): void {
        if (this.isSaving) {
            return;
        }

        this.showBudgetSettingsModal = false;
        this.showDeleteBudgetConfirmation = true;
    }

    @action
    closeDeleteBudgetConfirmationModal(): void {
        this.showDeleteBudgetConfirmation = false;
        this.showBudgetSettingsModal = true;
    }

    async refreshCurrentCycleConfigurationCycleBudgetFields(): Promise<void> {
        this.configurationsStore.clearCycleconfigurations();
        await this.configurationsStore.fetchCycleConfigurations();
    }

    @action
    async deleteBudget(budgetCycleId: number): Promise<void> {
        const deletedBudget = this.editableCycleConfiguration?.cycleBudgets.find(budget =>
            budget.budgetCycleId === budgetCycleId);
        
        if (!this.editableCycleConfiguration || !deletedBudget) {
            return;
        }

        await this._budgetServiceManager.deleteBudget(
            this.configurationsStore._masterAccountId,
            this.editableCycleConfiguration.cycleId,
            deletedBudget.budgetCycleId);
            
        this.configurationsStore.clearCycleconfigurations();
        await this.configurationsStore.fetchCycleConfigurations();
        this.setEditableCycleConfiguration();
        this.editableCycleConfiguration.removeBudget(deletedBudget);
        this.configurationsStore.convertedCycleSettingsViewCycle.removeBudget(deletedBudget);
        this.closeDeleteBudgetConfirmationModal();
        this.closeBudgetSettingsModal();
        this.resetView(true);
    }

    @action
    updateAssociatedAmounts(budget: CycleBudget): void {
        budget.clearFields(true);
        this.currentModalBudget?.associatedFields.forEach(selectedField => {
            const associatedAmount =
                this.editableCycleConfiguration?.cycleBudgetFields
                    .find(budgetField => budgetField.title === selectedField);
            if (!associatedAmount) {
                return;
            }
            this.overlappedFieldBudgets.forEach(overlappedFieldBudget => {
                overlappedFieldBudget.removeAssociatedField(associatedAmount);
            });

            budget.pushField(associatedAmount);
        });
    }

    @action
    checkForAutoSyncBudget(budget: CycleBudget): void {
        if (!budget.hasAnyLabAmount) {
            budget.setAutoSyncWithLab(false);
            return;
        }

        budget.setAutoSyncWithLab(true);
        budget.setLastSyncDate();
    }

    @action
    getAddedBudget(): CycleBudget {
        // make sure we have unique budget fields
        if (!this.currentModalBudget) {
            return {} as CycleBudget;
        }
        this.currentModalBudget.associatedFields = Array.from(new Set(this.currentModalBudget?.associatedFields));
        const addedBudget = this.currentModalBudget;
        this.updateAssociatedAmounts(addedBudget);
        this.checkForAutoSyncBudget(addedBudget);
        return addedBudget;
    }

    @action
    removeUnassociatedBudgetTypes(budgetCycleId: number): void {
        const budget = this.editableCycleConfiguration?.cycleBudgets.find(cycleBudget =>
            cycleBudget.budgetCycleId === budgetCycleId);
        if (!budget) {
            return;
        }
        budget.cycleBudgetFields.forEach((budgetField) => {
            if (!this.currentModalBudget?.associatedFields
                .some(associatedField => associatedField === budgetField.title)) {
                this.editableCycleConfiguration?.removeCycleBudgetField(budgetField.amountCode);
            }
        });
    }

    @action
    getUpdatedBudget(): CycleBudget {
        const editedBudget = this.editableCycleConfiguration?.cycleBudgets.find(budget =>
            budget.budgetCycleId === this.currentModalBudget?.budgetCycleId) as CycleBudget;
        if (!editedBudget) {
            return {} as CycleBudget;
        }
        
        editedBudget.updateBudget(this.currentModalBudget as CycleBudget);
        this.updateAssociatedAmounts(editedBudget);
        this.editableCycleConfiguration?.cycleBudgets.forEach(budget => new CycleBudget(budget).updateColor());
        return editedBudget;
    }

    @action
    async saveBudgetSettings(): Promise<void> {
        this.isSaving = true;
        if (!this.editableCycleConfiguration) {
            return;
        }
        try {
            if (this.currentModalView === BudgetSettingModalViews.Create) {
                this.removeEmptyRules();
                await configurationDataManager.saveBonusFieldRules(
                    this.bonusFieldRules,
                    this.configurationsStore._masterAccountId,
                    this.editableCycleConfiguration.cycleId.toString() ?? '');

                const addedBudget = this.getAddedBudget();
                const newBudgetCycleId = await this._budgetServiceManager.insertBudget(
                    this.configurationsStore.masterAccountId,
                    this.editableCycleConfiguration?.cycleId,
                    addedBudget as ICycleBudget);
                if (newBudgetCycleId) {
                    addedBudget.refreshBudgetCycleId(newBudgetCycleId);
                }
                
                this.editableCycleConfiguration?.addBudget(addedBudget as ICycleBudget);
                this.editableCycleConfiguration?.cycleBudgets.forEach(budget => new CycleBudget(budget).updateColor());
            }

            if (this.currentModalView === BudgetSettingModalViews.Edit) {
                this.removeEmptyRules();
                await configurationDataManager.saveBonusFieldRules(
                    this.bonusFieldRules,
                    this.configurationsStore._masterAccountId,
                    this.editableCycleConfiguration.cycleId.toString());

                this.removeUnassociatedBudgetTypes(this.currentModalBudget?.budgetCycleId ?? 0);
                const editedBudget = this.getUpdatedBudget();
                await this._budgetServiceManager.updateBudget(
                    this.configurationsStore._masterAccountId,
                    this.editableCycleConfiguration.cycleId,
                    editedBudget as ICycleBudget);

                const currentBudgetDto = this.editableCycleConfiguration?.cycleBudgets.find(budget =>
                    budget.budgetCycleId === this.currentModalBudget?.budgetCycleId);
                if (currentBudgetDto) {
                    const currentBudget = new CycleBudget(currentBudgetDto);
                    currentBudget.updateBudget(editedBudget);
                    if (this.onUpdateCallback) {
                        this.onUpdateCallback(currentBudget);
                    }
                }
            }

            await this.refreshCurrentCycleConfigurationCycleBudgetFields();
            this.isSaving = false;
            this.closeBudgetSettingsModal();
        } catch {
            this.isSaving = false;
        }
    }

    navigateToBudgetView(budget: CycleBudget): void {
        setCurrentBudgetView(this.currentBudgetCode(budget) ?? '', true);
    }

    updateBonusFieldRules(): Promise<void> {
        return configurationDataManager.saveBonusFieldRules(this.bonusFieldRules,
            this.configurationsStore._masterAccountId,
            this.editableCycleConfiguration?.cycleId.toString() ?? '');
    }

    @action
    setBudget(budget: CycleBudget): void {
        this.currentSetBudget = budget;
        this.currentModalBudget = budget;
    }

    getFieldCss(disabled: boolean): string {
        return (disabled) ? 'pxl-pill--secondary disabled' : 'pxl-pill--secondary';
    }

    fieldsThatExistInBudget(): string[] {
        const budgetId = this.currentModalBudget?.id;
        const existingBudget =
            this.editableCycleConfiguration?.cycleBudgets.find(budget => budget.id === budgetId);
        return existingBudget ? existingBudget.associatedFields : [];
    }

    @computed
    get title(): string {
        return this.currentModalView === BudgetSettingModalViews.Create ? 'Create Budget' : 'Budget Settings';
    }

    @computed
    get buttonClassName(): string {
        return 'pxl-btn pxl-margin-left-large ' + (this.isSaving ? '' : 'pxl-btn--primary');
    }

    @computed
    get requireCommentCss(): string {
        return this.currentModalBudget?.allowManagerOverBudget ? 'indent' : 'indent disabled';
    }

    @computed
    get saveButtonTitle(): string {
        if (this.isSaving) {
            return ' Saving ... ';
        }

        return this.currentModalView === BudgetSettingModalViews.Create ? 'Create Budget' : 'Save Changes';
    }

    @computed
    get saveButtonDisabled(): boolean {
        if (!this.editableCycleConfiguration || !this.currentModalBudget) {
            return true;
        }
        return this.isNotReadyToSave()
            || this.currentModalBudget.associatedFields?.length + this.fieldsThatExistInBudget().length === 0
            || this.currentModalBudget.name.length === 0
            || this.fieldsWithOtherProrationRule.length > 0
            || this.isSaving;
    }

    isNotReadyToSave(): boolean {
        if (!this.editableCycleConfiguration) {
            return true;

        }
        if (this.editableCycleConfiguration.endDate === undefined || this.editableCycleConfiguration.endDate === '') {
            return true;
        }

        if (this.editableCycleConfiguration.payEffectiveDate === undefined || this.editableCycleConfiguration.payEffectiveDate === '') {
            return true;
        }

        return false;
    }

    @computed
    get isFieldHasBeenUsed(): boolean {
        return this.overlappedFieldBudgets.size > 0;
    }

    @computed
    get associatedFieldsCss(): string {
        return this.getFieldCss(this.stockFieldSelected);
    }

    @computed
    get stockFieldCss(): string {
        if (!this.currentModalBudget || !this.currentModalBudget.associatedFields) {
            return '';
        }
        return this.getFieldCss(
            !this.stockFieldSelected && this.currentModalBudget.associatedFields.length > 0);
    }

    @computed
    get stockPillCss(): string {
        return this.stockFieldSelected ? 'pxl-pill pxl-pill-selected' : 'pxl-pill';
    }

    @computed
    get associatedFieldOptions(): string[] {
        return this.availableFields.filter(field => field !== this.stockField &&
            this.fieldsThatExistInBudget().indexOf(field) < 0);
    }

    @computed
    get shouldRenderDeleteButton(): boolean {
        return this.currentModalView === BudgetSettingModalViews.Edit
            && this.currentModalBudget?.isBudgetHasAnyDecision === false;
    }

    @action
    handleBudgetView(currentBudgetCode: string, currentConfigView: ConfigViews): void {
        this.currentBudgetView = currentBudgetCode;
        // if the user is expecting to be inside the budget view then set it up
        if (this.currentConvertedConfiguration && currentBudgetCode && currentConfigView === ConfigViews.Budget) {
            const currentCycleBudget = this.currentConvertedConfiguration.cycleBudgets.filter(budget =>{
                const cycleBudget = new CycleBudget(budget);
                return cycleBudget.topPriorityField && (cycleBudget.topPriorityField.amountCode === currentBudgetCode);
            }
            ).pop();
            const dto = {
                id: currentCycleBudget?.id,
                cycleId:  currentCycleBudget?.cycleId,
                name: currentCycleBudget?.name,
                allowManagerOverBudget: currentCycleBudget?.allowManagerOverBudget,
                requireCommentWhenOverBudget: currentCycleBudget?.requireCommentWhenOverBudget,
                useProration: currentCycleBudget?.useProration,
                associatedFields: currentCycleBudget?.associatedFields,
                isBudgetHasAnyDecision: currentCycleBudget?.isBudgetHasAnyDecision,
                amountsAlreadyHaveDecisions: currentCycleBudget?.amountsAlreadyHaveDecisions,
                cycleBudgetFields: currentCycleBudget?.cycleBudgetFields,
                autoSync: currentCycleBudget?.autoSync,
                lastSync: currentCycleBudget?.lastSync,
                budgetCycleId: currentCycleBudget?.budgetCycleId,
                color: currentCycleBudget?.color
            } as unknown as ICycleBudget;
            this.setBudget(new CycleBudget(dto));
        } else {
            // user has navigated away from inside the budget view
            return this.resetView();
        }
    }

    @action
    handlerulesPanelView(showPanel: boolean, rulesTab: EligibilityProrationTabs): void {
        const eligibilityStore = this.eligibilityProrationRuleView;
        showPanel ?
            eligibilityStore.openRuleDialogLocal() : eligibilityStore.closeRuleDialogLocal();
        eligibilityStore.changeRuleTabLocal(rulesTab);
        eligibilityStore.setShowEligiblityProrationRuleDialog(showPanel);
    }

    hasAssociatedAmount(amountCode: AmountCode): boolean {
        const cycleBudgetField = this.availableCycleBudgetFields.find(field => field.amountCode === amountCode);
        if (!cycleBudgetField || !this.currentModalBudget || !this.currentModalBudget.associatedFields) {
            return false;
        }

        return this.currentModalBudget.associatedFields
            .some(associatedField => associatedField === cycleBudgetField.title);
    }
}
